import React, { useState } from 'react';
import { Modal, Container, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { registerUserOrProvider } from '../firebase/registrationAuth';
import '../assets/css/registration-modal.css';

interface RegistrationModalProps {
  show: boolean;
  onHide: () => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ show, onHide }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [userTypeSelected, setUserTypeSelected] = useState(false);
  
  const [formData, setFormData] = useState({
    isServiceProvider: '',
    name: '',
    firstName: '',
    lastName: '',
    password: '',
    website: '',
    email: '',
    telephone: '',
    logoUrl: '',
    presentation: '',
    address: '',
    country: '',
    activityDomain: '',
    skills: [] as string[],
    activityDomains: [] as string[],
    certifications: [] as string[],
    languages: [] as string[]
  });

  const t = {
    fr: {
      choiceTitle: 'S\'inscrire',
      choiceSubtitle: 'Veuillez sélectionner votre type d\'utilisateur',
      title: 'Informations de l\'entreprise',
      subtitle: 'Créez votre compte prestataire en toute simplicité avec notre formulaire d\'inscription complet. Remplissez les détails essentiels de votre entreprise, y compris le nom de Societe, le logo, l\'adresse et les informations de contact. En complétant tous les champs requis, vous mettrez en valeur vos offres et améliorerez la communication avec les clients potentiels, garantissant le succès de votre entreprise sur notre plateforme.',
      userTitle: 'Informations personnelles',
      userSubtitle: 'Créez votre compte utilisateur en quelques étapes simples. Remplissez vos informations personnelles pour accéder à nos services.',
      serviceProvider: 'Êtes-vous un prestataire de services ?',
      yes: 'Oui',
      no: 'Non',
      backBtn: 'Retour',
      name: 'Nom',
      website: 'Site web',
      email: 'Email',
      telephone: 'Téléphone',
      logoUrl: 'URL du logo',
      presentation: 'Présentation',
      address: 'Adresse',
      country: 'Pays',
      activityDomain: 'Domaine d\'activité',
      skills: 'Compétences',
      skillsDesc: 'Sélectionnez vos compétences',
      activityDomains: 'Domaines d\'activités',
      activityDomainsDesc: 'Sélectionnez vos domaines d\'activités',
      certifications: 'Certifications',
      certificationsDesc: 'Sélectionnez vos certifications',
      languages: 'Langues',
      languagesDesc: 'Sélectionnez les langues que vous parlez',
      none: 'Aucun',
      submitBtn: 'Créer le compte',
      creatingBtn: 'Création en cours...',
      successMsg: 'Compte créé avec succès !',
      errorMsg: 'Erreur lors de la création du compte',
      closeBtn: 'Fermer'
    },
    en: {
      choiceTitle: 'Account Type',
      choiceSubtitle: 'Please select the type of account you want to create',
      title: 'Company Information',
      subtitle: 'Create your provider account effortlessly with our comprehensive registration form. Fill in essential company details, including name, logo, address, and contact information. By completing all required fields, you\'ll showcase your offerings and enhance communication with potential clients, ensuring your business is positioned for success on our platform.',
      userTitle: 'Personal Information',
      userSubtitle: 'Create your user account in a few simple steps. Fill in your personal information to access our services.',
      serviceProvider: 'Are you a service provider?',
      yes: 'Yes',
      no: 'No',
      backBtn: 'Back',
      name: 'Name',
      website: 'Website',
      email: 'Email',
      telephone: 'Phone',
      logoUrl: 'Logo URL',
      presentation: 'Presentation',
      address: 'Address',
      country: 'Country',
      activityDomain: 'Activity Domain',
      skills: 'Skills',
      skillsDesc: 'Select your skills',
      activityDomains: 'Activity Domains',
      activityDomainsDesc: 'Select your activity domains',
      certifications: 'Certifications',
      certificationsDesc: 'Select your certifications',
      languages: 'Languages',
      languagesDesc: 'Select the languages you speak',
      none: 'None',
      submitBtn: 'Create Account',
      creatingBtn: 'Creating...',
      successMsg: 'Account created successfully!',
      errorMsg: 'Error creating account',
      closeBtn: 'Close'
    }
  } as const;

  const tr = t[language];

  const handleRadioChange = (value: string) => {
    // Fermer la modale
    onHide();
    
    // Naviguer vers la page appropriée selon le choix
    if (value === 'yes') {
      navigate('/register-provider');
    } else {
      navigate('/register-user');
    }
  };

  const handleClose = () => {
    setUserTypeSelected(false);
    setFormData({
      isServiceProvider: '',
      name: '',
      firstName: '',
      lastName: '',
      password: '',
      website: '',
      email: '',
      telephone: '',
      logoUrl: '',
      presentation: '',
      address: '',
      country: '',
      activityDomain: '',
      skills: [],
      activityDomains: [],
      certifications: [],
      languages: []
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="custom-modal-header">
        <Modal.Title className="custom-modal-title">
          <i className="bi bi-person-plus me-2"></i>
          {tr.choiceTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container fluid>
          <div className="text-center mb-4">
            <p className="text-muted subtitle-aptos">{tr.choiceSubtitle}</p>
          </div>
          
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-center d-block mb-4 fs-5 question-aptos">
              Type d'utilisateur
            </Form.Label>
            <div className="d-flex justify-content-center gap-3">
              <Button
                variant="outline-primary"
                onClick={() => handleRadioChange('yes')}
                className="choice-button"
                style={{ minWidth: '180px' }}
              >
                <i className="bi bi-building me-2"></i>
                Prestataire
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => handleRadioChange('no')}
                className="choice-button"
                style={{ minWidth: '180px' }}
              >
                <i className="bi bi-person me-2"></i>
                Exportateur
              </Button>
            </div>
          </Form.Group>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default RegistrationModal;
